import type { ContextProfile } from '@/common/dataContext';

export type GettingStartedStatus = 'pending' | 'completed';

/** Snapshotted / campaign step action for checklist UI. */
export const GETTING_STARTED_STEP_ACTION_INVITE_TEAMMATE = 'invite_teammate' as const;
export const GETTING_STARTED_STEP_ACTION_ASSIGN_AGREEMENT_PROJECT =
  'assign_agreement_project' as const;
export const GETTING_STARTED_STEP_ACTION_SETUP_FOUNDER = 'setup_founder' as const;
export const GETTING_STARTED_STEP_ACTION_COMPANY_PERSONALIZATION =
  'company_personalization' as const;

export type GettingStartedStepAction =
  | typeof GETTING_STARTED_STEP_ACTION_INVITE_TEAMMATE
  | typeof GETTING_STARTED_STEP_ACTION_ASSIGN_AGREEMENT_PROJECT
  | typeof GETTING_STARTED_STEP_ACTION_SETUP_FOUNDER
  | typeof GETTING_STARTED_STEP_ACTION_COMPANY_PERSONALIZATION;

export type GettingStartedStep = {
  id: string;
  title: string;
  description: string | null;
  completed: boolean;
  completedAt: string | null;
  action?: GettingStartedStepAction | null;
};

export type GettingStartedState = {
  isFounder: boolean;
  status: GettingStartedStatus | null;
  campaignSlug: string | null;
  steps: GettingStartedStep[];
  /** Hard gate after values when pending with one or more steps. */
  shouldGate: boolean;
};

/**
 * Normalize optional step action from profile / API.
 *
 * @param raw Unknown action value
 */
function normalizeGettingStartedStepAction(
  raw: unknown
): GettingStartedStepAction | null {
  if (raw === GETTING_STARTED_STEP_ACTION_INVITE_TEAMMATE) {
    return GETTING_STARTED_STEP_ACTION_INVITE_TEAMMATE;
  }
  if (raw === GETTING_STARTED_STEP_ACTION_ASSIGN_AGREEMENT_PROJECT) {
    return GETTING_STARTED_STEP_ACTION_ASSIGN_AGREEMENT_PROJECT;
  }
  if (raw === GETTING_STARTED_STEP_ACTION_SETUP_FOUNDER) {
    return GETTING_STARTED_STEP_ACTION_SETUP_FOUNDER;
  }
  if (raw === GETTING_STARTED_STEP_ACTION_COMPANY_PERSONALIZATION) {
    return GETTING_STARTED_STEP_ACTION_COMPANY_PERSONALIZATION;
  }
  return null;
}

/**
 * Derive getting-started checklist state from the hydrated login profile.
 *
 * @param profile Authenticated context profile
 * @param authenticatedEmployeeID Session employee id
 * @param valuesSetupStillPending When true, getting-started gate stays off (values first)
 */
export function resolveGettingStartedState(
  profile: ContextProfile | null | undefined,
  authenticatedEmployeeID?: string | number | null,
  valuesSetupStillPending = false
): GettingStartedState {
  const selfOnboarding = profile?.companyInfo?.selfOnboarding;
  const currentEmployeeID =
    authenticatedEmployeeID ?? profile?.employeeInfo?.employeeID ?? profile?.employeeID;
  const isFounder =
    selfOnboarding?.status === 'active' &&
    selfOnboarding.founderEmployeeID != null &&
    String(currentEmployeeID) === String(selfOnboarding.founderEmployeeID);

  if (!isFounder) {
    return {
      isFounder: false,
      status: null,
      campaignSlug: null,
      steps: [],
      shouldGate: false,
    };
  }

  const raw = selfOnboarding.gettingStarted;
  const steps: GettingStartedStep[] = Array.isArray(raw?.steps)
    ? raw.steps
        .filter((step) => step && typeof step.id === 'string' && typeof step.title === 'string')
        .map((step) => {
          const mapped: GettingStartedStep = {
            id: step.id,
            title: step.title,
            description: step.description ?? null,
            completed: Boolean(step.completed),
            completedAt: step.completedAt ?? null,
          };
          const action = normalizeGettingStartedStepAction(
            (step as { action?: unknown }).action
          );
          if (action) {
            mapped.action = action;
          }
          return mapped;
        })
    : [];

  const status: GettingStartedStatus | null =
    steps.length === 0
      ? null
      : raw?.status === 'completed'
        ? 'completed'
        : 'pending';

  const campaignSlug =
    typeof raw?.campaignSlug === 'string' && raw.campaignSlug.trim()
      ? raw.campaignSlug.trim()
      : null;

  return {
    isFounder: true,
    status,
    campaignSlug,
    steps,
    shouldGate:
      !valuesSetupStillPending && status === 'pending' && steps.length > 0 && Boolean(campaignSlug),
  };
}

/** Path allowlist while getting-started hard gate is active. */
export function isGettingStartedAllowlistedPath(pathname: string): boolean {
  return (
    pathname.startsWith('/getting-started/') ||
    pathname === '/setup/company-values' ||
    pathname.startsWith('/settings')
  );
}

/**
 * Canonical checklist URL for a campaign slug.
 *
 * @param campaignSlug Snapshotted campaign slug
 */
export function buildGettingStartedPath(campaignSlug: string): string {
  return `/getting-started/${encodeURIComponent(campaignSlug)}`;
}
